import { createContext, useContext, useMemo } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuth } from '@/providers/auth-provider'
import { HubApiClient } from '@/lib/api/client'

const ApiContext = createContext<HubApiClient | null>(null)

export function useApi(): HubApiClient {
  const ctx = useContext(ApiContext)
  if (!ctx) throw new Error('useApi must be used within ApiProvider')
  return ctx
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  })
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (!browserQueryClient) browserQueryClient = makeQueryClient()
  return browserQueryClient
}

export function ApiProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth()
  const queryClient = getQueryClient()

  const apiClient = useMemo(() => {
    return new HubApiClient('/api', () => token)
  }, [token])

  return (
    <ApiContext.Provider value={apiClient}>
      <QueryClientProvider client={queryClient}>
        {children}
      </QueryClientProvider>
    </ApiContext.Provider>
  )
}
